import { Controller } from "@hotwired/stimulus"
import { csrfToken } from "../utils/csrf"

// LinkedIn / WhatsApp thread composer: auto-growing textarea, local draft
// persistence, and async "Draft with Jane". The keyboard belongs to
// composer-submit, which shared/_message_composer attaches to every composer —
// including the ones that never load this controller.
export default class extends Controller {
    static targets = ["field", "draftBtn", "sendBtn", "status"]
    static values = { draftUrl: String, storageKey: String }

    connect() {
        this._drafting = false
        this._restoreDraft()
        this._resize()
        this.fieldTarget.addEventListener("input", () => {
            this._resize()
            this._persistDraft()
        })
    }

    disconnect() {
        if (this._saveTimer) clearTimeout(this._saveTimer)
    }

    send(event) {
        if (!this._hasText()) {
            event.preventDefault()
            this._setStatus("Write a message first.")
            this.fieldTarget.focus()
            return
        }
        this._clearDraftStorage()
        this._setStatus("")
    }

    async draft(event) {
        event.preventDefault()
        if (this._drafting) return

        this._drafting = true
        this._setDrafting(true)
        this._setStatus("Jane is drafting…")

        try {
            const response = await fetch(this.draftUrlValue, {
                method: "POST",
                headers: {
                    "Accept": "application/json",
                    "X-CSRF-Token": csrfToken(),
                    "Content-Type": "application/json"
                },
                credentials: "same-origin",
                body: JSON.stringify({})
            })
            const data = await response.json()
            if (!response.ok || !data.ok) throw new Error(data.error || "Draft failed")

            this.fieldTarget.value = data.body || ""
            this._resize()
            this._persistDraft()
            this._setStatus("Draft ready - edit before sending.")
            this.fieldTarget.focus()
        } catch (err) {
            this._setStatus(err.message || "Could not draft a reply.")
        } finally {
            this._drafting = false
            this._setDrafting(false)
        }
    }

    _resize() {
        let field = this.fieldTarget
        field.style.height = "auto"
        field.style.height = `${Math.min(field.scrollHeight, 200)}px`
    }

    _restoreDraft() {
        if (!this.storageKeyValue) return
        try {
            let saved = localStorage.getItem(this.storageKeyValue)
            if (saved && !this.fieldTarget.value) this.fieldTarget.value = saved
        } catch {
            // private browsing
        }
    }

    _persistDraft() {
        if (!this.storageKeyValue) return
        if (this._saveTimer) clearTimeout(this._saveTimer)
        this._saveTimer = setTimeout(() => {
            try {
                let text = this.fieldTarget.value
                if (text.trim()) localStorage.setItem(this.storageKeyValue, text)
                else localStorage.removeItem(this.storageKeyValue)
            } catch {
                // ignore
            }
        }, 300)
    }

    _clearDraftStorage() {
        if (!this.storageKeyValue) return
        try {
            localStorage.removeItem(this.storageKeyValue)
        } catch {
            // ignore
        }
    }

    _hasText() {
        return this.fieldTarget.value.trim().length > 0
    }

    _setDrafting(active) {
        if (this.hasDraftBtnTarget) {
            this.draftBtnTarget.disabled = active
            this.draftBtnTarget.classList.toggle("is-loading", active)
        }
        if (this.hasSendBtnTarget) this.sendBtnTarget.disabled = active
    }

    _setStatus(message) {
        if (!this.hasStatusTarget) return
        this.statusTarget.textContent = message || ""
    }

}
